import { DeviceIdentificationNumber } from '../value-objects/DeviceIdentificationNumber';

/**
 * APIレスポンスから受け取る盤データの型定義
 *
 * インフラストラクチャ層とドメイン層で共通して使用する。
 */
export interface PanelApiData {
  readonly id: number;
  readonly panelName: string;
  readonly siteName: string;
  readonly status: 'NORMAL' | 'STOPPED' | 'ERROR' | null;
  readonly deviceGroupNumber: number | null;
  readonly deviceNumber: number | null;
}

/**
 * Panelエンティティのコンストラクタパラメータ
 */
interface PanelConstructorParams {
  readonly id: number;
  readonly panelName: string;
  readonly siteName: string;
  readonly status: 'NORMAL' | 'STOPPED' | 'ERROR' | null;
  readonly deviceGroupNumber: number | null;
  readonly deviceNumber: number | null;
  readonly deviceIdentification: DeviceIdentificationNumber;
}

/**
 * 盤エンティティ
 *
 * 盤の基本情報とビジネスロジックを保持する。
 * 値オブジェクトの生成はこのエンティティ内で完結する。
 */
export class Panel {
  private readonly id: number;
  private readonly panelName: string;
  private readonly siteName: string;
  private readonly status: 'NORMAL' | 'STOPPED' | 'ERROR' | null;
  private readonly deviceGroupNumber: number | null;
  private readonly deviceNumber: number | null;
  private readonly deviceIdentification: DeviceIdentificationNumber;

  private constructor(params: PanelConstructorParams) {
    // NOTE: 現在のパラメータは全てプリミティブ型（number, string, null）および
    // 不変な値オブジェクト（DeviceIdentificationNumber）のため、
    // 値渡しで実質的にDeepCopyと同等の動作となります。
    // 将来的にオブジェクトや配列のプロパティを追加する場合は、
    // 明示的なDeepCopy処理（structuredClone等）を実装してください。
    this.id = params.id;
    this.panelName = params.panelName;
    this.siteName = params.siteName;
    this.status = params.status;
    this.deviceGroupNumber = params.deviceGroupNumber;
    this.deviceNumber = params.deviceNumber;
    this.deviceIdentification = params.deviceIdentification;
  }

  /**
   * APIレスポンスから盤エンティティを生成するファクトリメソッド
   *
   * 値オブジェクトの生成はこのドメイン層メソッド内で完結する。
   */
  public static createFromApiResponse(apiData: PanelApiData): Panel {
    const deviceIdentification = DeviceIdentificationNumber.create({
      deviceGroupNumber: apiData.deviceGroupNumber,
      deviceNumber: apiData.deviceNumber,
    });

    return new Panel({
      id: apiData.id,
      panelName: apiData.panelName,
      siteName: apiData.siteName,
      status: apiData.status,
      deviceGroupNumber: apiData.deviceGroupNumber,
      deviceNumber: apiData.deviceNumber,
      deviceIdentification,
    });
  }

  /**
   * ID取得
   */
  public getId(): number {
    return this.id;
  }

  /**
   * 盤名取得
   */
  public getPanelName(): string {
    return this.panelName;
  }

  /**
   * サイト名取得
   */
  public getSiteName(): string {
    return this.siteName;
  }

  /**
   * ステータス取得
   */
  public getStatus(): 'NORMAL' | 'STOPPED' | 'ERROR' | null {
    return this.status;
  }

  /**
   * デバイスグループ番号取得
   */
  public getDeviceGroupNumber(): number | null {
    return this.deviceGroupNumber;
  }

  /**
   * デバイス番号取得
   */
  public getDeviceNumber(): number | null {
    return this.deviceNumber;
  }

  /**
   * デバイス識別番号の表示文字列取得
   */
  public getDeviceIdentificationDisplayText(): string {
    return this.deviceIdentification.toDisplayString();
  }

  /**
   * プレゼンテーション層で使用するデータ構造に変換
   *
   * アプリケーション層/UI層で必要な形式のプレーンオブジェクトを返す。
   */
  public toPresentationModel(): {
    readonly id: number;
    readonly panelName: string;
    readonly siteName: string;
    readonly status: 'NORMAL' | 'STOPPED' | 'ERROR' | null;
    readonly deviceGroupNumber: number | null;
    readonly deviceNumber: number | null;
    readonly deviceIdentificationDisplayText: string;
  } {
    return {
      id: this.id,
      panelName: this.panelName,
      siteName: this.siteName,
      status: this.status,
      deviceGroupNumber: this.deviceGroupNumber,
      deviceNumber: this.deviceNumber,
      deviceIdentificationDisplayText: this.deviceIdentification.toDisplayString(),
    };
  }
}
